import { ReactNode, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { client } from '@frontend/features/common/services';
import { CONFIG } from '@frontend/config';
import { catalogAdminKey, catalogAdminPath } from './queries';
import { DiscoveryPage, discoveryPending } from './discovery';
import { AdoptionHotel, AdoptionJob, AdoptionRequest, AdoptionSelection, adoptionCsvIds } from './adoption';
const root = 'hotel-catalog-admin/adoptions';
export function CatalogAdoption({
  discoveryId,
  complete,
  active,
  selected,
  onSelect,
  children,
}: {
  discoveryId: string;
  complete: boolean;
  active: boolean;
  selected: number[];
  onSelect(ids: number[]): void;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const label = (k: string, options?: Record<string, unknown>) => t(`hotelAdoption.${k}`, options);
  const cache = useQueryClient();
  const storageKey = `catalog-adoption-pending:${CONFIG.AGENCY_ID}:${discoveryId}`;
  const [pending, setPending] = useState<AdoptionRequest | null>(() => {
    try {
      const v = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
      return v?.discovery_job_id === discoveryId ? v : null;
    } catch {
      return null;
    }
  });
  const [selection, setSelection] = useState<AdoptionSelection | null>(null),
    [prefer, setPrefer] = useState(true),
    [reason, setReason] = useState('');
  const [jobId, setJobId] = useState(''),
    [inputError, setInputError] = useState('');
  const [view, setView] = useState('received');
  const saving = useRef(false);
  const history = useQuery({
    queryKey: [...catalogAdminKey, 'adoptions', discoveryId],
    enabled: active,
    retry: false,
    queryFn: async () =>
      (
        await client.get<{ data: Pick<AdoptionJob, 'job_id' | 'status' | 'created_at'>[] }>(root, {
          params: { discovery_job_id: discoveryId },
        })
      ).data.data,
  });
  const create = useMutation({
    retry: false,
    mutationFn: async (body: AdoptionRequest) => (await client.post<{ data: AdoptionJob }>(root, body)).data.data,
    onSuccess: (data) => {
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        /* Replaying the UUID remains safe. */
      }
      setPending(null);
      setSelection(null);
      setJobId(data.job_id);
      setView('adoptions');
      onSelect([]);
      cache.invalidateQueries([...catalogAdminKey, 'adoptions', discoveryId]);
    },
    onSettled: () => {
      saving.current = false;
    },
  });
  const start = () => {
    if (saving.current || (!pending && (!selection || !reason.trim()))) return;
    const body = pending || {
      request_id: crypto.randomUUID(),
      discovery_job_id: discoveryId,
      selection: selection!,
      prefer_source_content: prefer,
      reason: reason.trim(),
    };
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(body));
    } catch {
      setInputError('storageError');
      return;
    }
    setInputError('');
    setPending(body);
    saving.current = true;
    create.mutate(body);
  };
  const importFile = async (file?: File) => {
    if (!file) return;
    try {
      if (file.size > 5_000_000) throw new Error('size');
      onSelect(adoptionCsvIds(await file.text()));
      setInputError('');
    } catch {
      setInputError('csvError');
    }
  };
  const currentId = jobId || history.data?.[0]?.job_id;
  return (
    <Stack spacing={2} sx={{ mt: 3, '& input, & .MuiSelect-select': { fontSize: '16px !important' } }}>
      <Tabs
        value={view}
        onChange={(_, next) => setView(next)}
        aria-label={label('views')}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab value="received" label={label('receivedTab')} />
        <Tab value="adoptions" label={label('adoptionsTab')} />
      </Tabs>
      <Box hidden={view !== 'received'} role="tabpanel">
        <Stack spacing={2}>
          <Typography variant="h6">{label('title')}</Typography>
          <Typography color="text.secondary">{label('help')}</Typography>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Button
              variant="contained"
              disabled={!complete || !!pending || create.isLoading}
              onClick={() => setSelection({ mode: 'all' })}
            >
              {label('keepAll')}
            </Button>
            <Button
              variant="outlined"
              disabled={!complete || !selected.length || !!pending || create.isLoading}
              onClick={() => setSelection({ mode: 'ids', supplier_hotel_ids: selected })}
            >
              {label('keepSelected', { count: selected.length })}
            </Button>
            <Button component="label" disabled={!complete || !!pending}>
              {label('importIds')}
              <input
                hidden
                type="file"
                accept=".csv,.txt,.tsv,text/csv,text/plain"
                onChange={(e) => {
                  importFile(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </Button>
            {!!selected.length && (
              <Button disabled={!!pending} onClick={() => onSelect([])}>
                {label('clearSelection')}
              </Button>
            )}
          </Stack>
          {inputError && <Alert severity="error">{label(inputError)}</Alert>}
          {children}
        </Stack>
      </Box>
      {pending && (
        <Alert
          severity={create.isError ? 'warning' : 'info'}
          action={
            <Button disabled={create.isLoading} onClick={start}>
              {label('recover')}
            </Button>
          }
        >
          {label('pending')}
        </Alert>
      )}
      {pending && [400, 404, 409, 413, 422].includes((create.error as any)?.response?.status) && (
        <Button
          onClick={() => {
            try {
              sessionStorage.removeItem(storageKey);
              setPending(null);
              create.reset();
            } catch {
              setInputError('storageError');
            }
          }}
        >
          {label('correct')}
        </Button>
      )}
      {create.isError && !pending && <Alert severity="error">{label('saveError')}</Alert>}
      <Dialog open={selection !== null && !pending} onClose={() => setSelection(null)} fullWidth maxWidth="sm">
        <DialogTitle>{label('confirmTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography>
              {selection?.mode === 'all'
                ? label('allScope')
                : label('selectedScope', { count: selection?.supplier_hotel_ids.length })}
            </Typography>
            <Alert severity="info">{label('inactiveHelp')}</Alert>
            <FormControlLabel
              control={<Checkbox checked={prefer} onChange={(e) => setPrefer(e.target.checked)} />}
              label={label('prefer')}
            />
            <TextField
              label={label('reason')}
              value={reason}
              inputProps={{ maxLength: 512 }}
              onChange={(e) => setReason(e.target.value)}
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelection(null)}>{label('cancel')}</Button>
          <Button variant="contained" disabled={!reason.trim() || create.isLoading} onClick={start}>
            {label('start')}
          </Button>
        </DialogActions>
      </Dialog>
      <Box hidden={view !== 'adoptions'} role="tabpanel">
        <Stack spacing={2}>
          <Button onClick={() => history.refetch()}>{label('reload')}</Button>
          {history.isError && (
            <Alert severity="error" action={<Button onClick={() => history.refetch()}>{label('reload')}</Button>}>
              {label('loadError')}
            </Alert>
          )}
          {!!history.data?.length && (
            <TextField
              select
              label={label('history')}
              value={history.data.some((j) => j.job_id === currentId) ? currentId : ''}
              onChange={(e) => setJobId(e.target.value)}
            >
              {history.data.map((j) => (
                <MenuItem key={j.job_id} value={j.job_id}>
                  {new Date(j.created_at).toLocaleString()} · {t(`hotelDiscovery.status.${j.status}`)}
                </MenuItem>
              ))}
            </TextField>
          )}
          {!history.isFetching && !currentId && <Typography>{label('noRuns')}</Typography>}
          {currentId && <AdoptionResults key={currentId} jobId={currentId} active={active && view === 'adoptions'} />}
        </Stack>
      </Box>
    </Stack>
  );
}
export function AdoptionResults({ jobId, active }: { jobId: string; active: boolean }) {
  const { t } = useTranslation();
  const label = (k: string, options?: Record<string, unknown>) => t(`hotelAdoption.${k}`, options);
  const cache = useQueryClient(),
    navigate = useNavigate();
  const [status, setStatus] = useState(''),
    [offset, setOffset] = useState(0);
  const [review, setReview] = useState<AdoptionHotel | null>(null),
    [target, setTarget] = useState(''),
    [distinct, setDistinct] = useState(false),
    [reason, setReason] = useState('');
  const job = useQuery({
    queryKey: [...catalogAdminKey, 'adoption', jobId],
    enabled: active,
    retry: false,
    queryFn: async () => (await client.get<{ data: AdoptionJob }>(`${root}/${encodeURIComponent(jobId)}`)).data.data,
    refetchInterval: (data) => (active && discoveryPending(data?.status) ? 5000 : false),
    refetchIntervalInBackground: false,
  });
  const hotels = useQuery({
    queryKey: [...catalogAdminKey, 'adoption-hotels', jobId, status, offset, job.data?.updated_at],
    enabled: active,
    retry: false,
    queryFn: async () =>
      (
        await client.get<DiscoveryPage<AdoptionHotel>>(`${root}/${encodeURIComponent(jobId)}/hotels`, {
          params: { limit: 25, offset, ...(status ? { status } : {}) },
        })
      ).data,
  });
  const refreshCatalog = () => {
    ['inventory', 'inventory-options', 'detail', 'connections', 'discovered-hotels'].forEach((k) =>
      cache.invalidateQueries([...catalogAdminKey, k]),);
  };
  useEffect(() => {
    if (job.data?.status === 'completed') {
      refreshCatalog();
      cache.invalidateQueries([...catalogAdminKey, 'adoptions']);
    }
  }, [job.data?.status, jobId]);
  const decide = useMutation({
    retry: false,
    mutationFn: async () =>
      client.post(`${root}/${encodeURIComponent(jobId)}/hotels/${review!.supplier_hotel_id}/decision`, {
        expected_version: review!.version,
        source_revision: review!.source_revision,
        action: target
          ? { kind: 'link', hotel_id: target.trim() }
          : { kind: 'create', confirm_distinct_property: distinct },
        reason: reason.trim(),
      }),
    onSuccess: () => {
      setReview(null);
      cache.invalidateQueries([...catalogAdminKey, 'adoption', jobId]);
      cache.invalidateQueries([...catalogAdminKey, 'adoption-hotels', jobId]);
      refreshCatalog();
    },
  });
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={2}>
        <Typography variant="h6">{label('results')}</Typography>
        {job.isError && (
          <Alert severity="error" action={<Button onClick={() => job.refetch()}>{label('reload')}</Button>}>
            {label('loadError')}
          </Alert>
        )}
        {job.data && (
          <>
            <Chip sx={{ alignSelf: 'flex-start' }} label={t(`hotelDiscovery.status.${job.data.status}`)} />
            <Stack direction="row" gap={1} flexWrap="wrap">
              {(['created', 'linked', 'existing', 'needs_review', 'pending'] as const).map((k) => (
                <Chip key={k} variant="outlined" label={`${label(`status.${k}`)}: ${job.data!.counts[k]}`} />
              ))}
            </Stack>
            {discoveryPending(job.data.status) && <LinearProgress />}
            {job.data.status === 'terminal_failed' && <Alert severity="warning">{label('failed')}</Alert>}
            {job.data.counts.needs_review > 0 && <Alert severity="warning">{label('reviewHelp')}</Alert>}
          </>
        )}
        <TextField
          select
          label={label('filter')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setOffset(0);
          }}
        >
          <MenuItem value="">{label('allResults')}</MenuItem>
          {['created', 'linked', 'existing', 'needs_review', 'pending'].map((k) => (
            <MenuItem key={k} value={k}>
              {label(`status.${k}`)}
            </MenuItem>
          ))}
        </TextField>
        {hotels.isFetching && <LinearProgress />}
        {hotels.isError && (
          <Alert severity="error" action={<Button onClick={() => hotels.refetch()}>{label('reload')}</Button>}>
            {label('loadError')}
          </Alert>
        )}
        {hotels.data?.data.map((h) => (
          <Box
            key={h.supplier_hotel_id}
            sx={{ py: 2, borderBottom: 1, borderColor: 'divider', overflowWrap: 'anywhere' }}
          >
            <Stack spacing={1}>
              <Typography fontWeight={600}>{h.display_name || h.hotel_code}</Typography>
              <Typography color="text.secondary">
                {[h.locality, h.country_code, h.address].filter(Boolean).join(' · ')}
              </Typography>
              <Typography variant="caption">{h.hotel_code}</Typography>
              <Chip size="small" sx={{ alignSelf: 'flex-start' }} label={label(`status.${h.status}`)} />
              {h.review_code && <Typography variant="body2">{label(`review.${h.review_code}`)}</Typography>}
              {h.hotel_id && (
                <Button
                  sx={{ alignSelf: 'flex-start' }}
                  onClick={() => navigate(`${catalogAdminPath}/hotels/${encodeURIComponent(h.hotel_id!)}`)}
                >
                  {label('openHotel')}
                </Button>
              )}
              {h.status === 'needs_review' && (
                <Button
                  sx={{ alignSelf: 'flex-start' }}
                  onClick={() => {
                    setReview(h);
                    setTarget('');
                    setDistinct(false);
                    setReason('');
                    decide.reset();
                  }}
                >
                  {label('reviewAction')}
                </Button>
              )}
            </Stack>
          </Box>
        ))}
        {hotels.data?.data.length === 0 && <Typography>{label('empty')}</Typography>}
        <Stack direction="row" gap={2}>
          <Button disabled={!offset || hotels.isFetching} onClick={() => setOffset((v) => Math.max(0, v - 25))}>
            {t('hotelCatalogAdmin.previous')}
          </Button>
          <Button
            disabled={!hotels.data?.meta.has_more || hotels.isFetching || offset >= 10000000}
            onClick={() => setOffset((v) => v + 25)}
          >
            {t('hotelCatalogAdmin.next')}
          </Button>
        </Stack>
        <Dialog
          open={!!review}
          onClose={() => {
            if (!decide.isLoading) setReview(null);
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{label('reviewAction')}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography fontWeight={600}>{review?.display_name}</Typography>
              <Typography>{review?.address}</Typography>
              {review?.candidate_hotel_ids.map((id) => (
                <Stack key={id} direction="row" gap={1} flexWrap="wrap">
                  <Button
                    onClick={() => {
                      setTarget(id);
                      setDistinct(false);
                    }}
                  >
                    {label('chooseCandidate')}: {id}
                  </Button>
                  <Button
                    component="a"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`${catalogAdminPath}/hotels/${encodeURIComponent(id)}`}
                  >
                    {label('inspectCandidate')}
                  </Button>
                </Stack>
              ))}
              {review && <Alert severity="info">{label(`review.${review.review_code}`)}</Alert>}
              <TextField
                label={label('existingId')}
                value={target}
                inputProps={{ maxLength: 512 }}
                onChange={(e) => {
                  setTarget(e.target.value);
                  setDistinct(false);
                }}
                disabled={
                  decide.isLoading ||
                  review?.review_code === 'conflicting_identifiers' ||
                  review?.review_code === 'connection_disabled'
                }
              />
              {!target && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={distinct}
                      disabled={
                        decide.isLoading ||
                        review?.review_code === 'conflicting_identifiers' ||
                        review?.review_code === 'connection_disabled'
                      }
                      onChange={(e) => setDistinct(e.target.checked)}
                    />
                  }
                  label={label('confirmDistinct')}
                />
              )}
              <TextField
                label={label('reason')}
                value={reason}
                inputProps={{ maxLength: 512 }}
                onChange={(e) => setReason(e.target.value)}
                multiline
                minRows={2}
                disabled={decide.isLoading}
              />
              {decide.isError && (
                <Alert severity="error">
                  {label((decide.error as any)?.response?.status === 409 ? 'conflict' : 'saveError')}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={decide.isLoading} onClick={() => setReview(null)}>
              {label('cancel')}
            </Button>
            <Button
              variant="contained"
              disabled={
                decide.isLoading ||
                !reason.trim() ||
                (!target.trim() && !distinct) ||
                review?.review_code === 'conflicting_identifiers' ||
                review?.review_code === 'connection_disabled'
              }
              onClick={() => decide.mutate()}
            >
              {label('saveDecision')}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Paper>
  );
}
